import { useCallback, useMemo, useState } from 'react';
import type { UserProfileAggregate } from '@leagueplatform/user-profile-api';
import { toMemberProfile } from '../../domain/profile/profile-wire';
import type { SectionValues } from '../../domain/profile/profile-types';
import { ACCOUNT_SETUP_FIELDS } from './account-setup-fields.config';
import { submitAccountSetup } from './account-setup-wire';

export const useAccountSetup = (profile: UserProfileAggregate) => {
  const memberProfile = useMemo(() => toMemberProfile(profile), [profile]);

  const [values, setValues] = useState<SectionValues>({
    mobilePhone: memberProfile.contactInformation.mobilePhone,
    primaryPhone: memberProfile.contactInformation.primaryPhone,
    emailAddress: memberProfile.contactInformation.emailAddress,
  });
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<Error | null>(null);

  const fullName = [
    memberProfile.personalInformation.firstName,
    memberProfile.personalInformation.lastName,
  ]
    .filter(Boolean)
    .join(' ');

  const onFieldChange = useCallback((id: string, value: string) => {
    setValues((previous) => ({ ...previous, [id]: value }));
    setErrors((previous) => ({ ...previous, [id]: undefined }));
  }, []);

  const validate = useCallback((): boolean => {
    const nextErrors: Record<string, string | undefined> = {};
    ACCOUNT_SETUP_FIELDS.forEach((field) => {
      nextErrors[field.id] = field.validate?.(values[field.id] ?? '');
    });
    setErrors(nextErrors);
    return Object.values(nextErrors).every((error) => !error);
  }, [values]);

  const submit = useCallback(async (): Promise<boolean> => {
    if (!validate()) return false;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await submitAccountSetup(values, profile, marketingOptIn);
      return true;
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error
          : new Error('account setup submit failed'),
      );
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [values, marketingOptIn, validate, profile]);

  return {
    fullName,
    fields: ACCOUNT_SETUP_FIELDS,
    values,
    errors,
    onFieldChange,
    marketingOptIn,
    setMarketingOptIn,
    isSubmitting,
    submitError,
    submit,
  };
};
